import { useEffect, useRef, useCallback } from 'react'
import debounce from 'lodash/debounce'
import { apiClient } from '@/services/api'
import type { TravelFormState, UpdateDraftRequest, CreateDraftRequest } from '@/types/travel'

const DEBOUNCE_DELAY = 3000
const RETRY_DELAY = 1000

interface UseAutoSaveParams {
  formData: TravelFormState
  savedDraftId: string | number | null
  /** 轻量校验：只要有足够数据就保存草稿（不要求全部必填） */
  canSaveDraft: () => boolean
  /** 用于构建完整的创建草稿请求（包含基础信息） */
  buildCreateDraftRequest: () => CreateDraftRequest
  onSaveSuccess: (id: number) => void
  onSaveError: (error: Error) => void
}

interface UseAutoSaveReturn {
  /** 加载数据后调用，更新内部数据快照，防止加载完成后立即触发自动保存 */
  markAsSaved: (data: TravelFormState) => void
  /**
   * 取消所有待执行的自动保存（debounce + retry）。
   * 提交前调用，防止提交成功后 in-flight 的保存请求触发"自动保存失败"提示。
   */
  cancelPendingSaves: () => void
  /** 提交失败后恢复自动保存 */
  resumeAutoSave: () => void
}

/**
 * 自动保存草稿 Hook（FR-027）
 *
 * 监听 formData 变化，必填字段完整后 3s 防抖触发保存。
 * 草稿阶段采用 Last-Write-Wins 策略，不做乐观锁校验。
 *
 * 竞态保护：createDraft 进行中时，若有新的保存请求到来（如切换同城/跨城），
 * 暂存最新数据到 pendingUpdateRef，等 create 完成后立即用新 ID 触发一次 update，
 * 避免并发 createDraft 产生多条草稿。
 */
const useAutoSave = ({
  formData,
  savedDraftId,
  canSaveDraft,
  buildCreateDraftRequest,
  onSaveSuccess,
  onSaveError,
}: UseAutoSaveParams): UseAutoSaveReturn => {
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstRenderRef = useRef(true)
  const lastSavedDataRef = useRef<string>('')
  // 提交时设为 true，屏蔽后续 in-flight 请求的错误回调
  const isCancelledRef = useRef(false)
  // createDraft 进行中标志，防止并发创建多条草稿
  const pendingCreateRef = useRef(false)
  // createDraft 进行中时收到的最新 formData，create 完成后用新 ID 立即 update
  const pendingUpdateDataRef = useRef<TravelFormState | null>(null)

  // 用 ref 存储所有外部回调的最新引用，每次渲染同步更新
  // 这样 doSave/debouncedSave 不需要依赖这些函数，避免频繁重新创建
  const callbacksRef = useRef({ onSaveSuccess, onSaveError, buildCreateDraftRequest, canSaveDraft })
  useEffect(() => {
    callbacksRef.current = { onSaveSuccess, onSaveError, buildCreateDraftRequest, canSaveDraft }
  })

  // doSave 通过 callbacksRef 调用最新回调，自身依赖稳定，不会频繁重新创建
  const doSave = useCallback(async (data: TravelFormState, draftId: number | null, isRetry = false) => {
    try {
      if (draftId) {
        // 已有草稿 ID：直接更新
        const fullData = callbacksRef.current.buildCreateDraftRequest()
        const request: UpdateDraftRequest = { ...fullData }
        const response = await apiClient.updateDraft(draftId, request)
        const result = response.data
        callbacksRef.current.onSaveSuccess(result.id)
        lastSavedDataRef.current = JSON.stringify(data)
      } else {
        // 无草稿 ID：需要创建新草稿
        // 若已有 createDraft 在飞行中，暂存最新数据，等 create 完成后再处理
        if (pendingCreateRef.current) {
          pendingUpdateDataRef.current = data
          return
        }

        pendingCreateRef.current = true
        try {
          const request = callbacksRef.current.buildCreateDraftRequest()
          const response = await apiClient.createDraft(request)
          const result = response.data
          callbacksRef.current.onSaveSuccess(result.id)
          lastSavedDataRef.current = JSON.stringify(data)

          // create 期间若有新数据变化，用新 ID 立即触发一次 update
          if (pendingUpdateDataRef.current) {
            const pendingData = pendingUpdateDataRef.current
            pendingUpdateDataRef.current = null
            doSaveRef.current(pendingData, result.id)
          }
        } finally {
          pendingCreateRef.current = false
        }
      }
    } catch (err) {
      if (isCancelledRef.current) return  // 提交后屏蔽错误
      const error = err instanceof Error ? err : new Error('自动保存失败')
      if (!isRetry) {
        retryTimerRef.current = setTimeout(() => {
          if (!isCancelledRef.current) doSave(data, draftId, true)
        }, RETRY_DELAY)
      } else {
        callbacksRef.current.onSaveError(error)
      }
    }
  }, [])

  // 用 ref 存储最新的 doSave，让 debouncedSave 只创建一次
  const doSaveRef = useRef(doSave)
  useEffect(() => {
    doSaveRef.current = doSave
  }, [doSave])

  // debouncedSave 整个生命周期只创建一次，通过 doSaveRef 调用最新的 doSave
  // 关键修复：避免因依赖变化重新创建 debounce 实例导致防抖失效
  const debouncedSave = useRef(
    debounce((data: TravelFormState, draftId: number | null) => {
      doSaveRef.current(data, draftId)
    }, DEBOUNCE_DELAY)
  ).current

  // 组件卸载时统一清理
  useEffect(() => {
    return () => {
      debouncedSave.cancel()
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
      }
    }
  }, [debouncedSave])

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      return
    }

    // 轻量校验：只要有足够数据就保存草稿，不要求全部必填
    if (!callbacksRef.current.canSaveDraft()) return

    const currentDataSnapshot = JSON.stringify(formData)
    if (currentDataSnapshot === lastSavedDataRef.current) return

    const draftId = savedDraftId
      ? (typeof savedDraftId === 'string' ? parseInt(savedDraftId, 10) : savedDraftId)
      : null
    if (draftId !== null && isNaN(draftId)) return

    debouncedSave(formData, draftId)
  }, [formData, savedDraftId, debouncedSave])

  const markAsSaved = useCallback((data: TravelFormState) => {
    lastSavedDataRef.current = JSON.stringify(data)
  }, [])

  const cancelPendingSaves = useCallback(() => {
    isCancelledRef.current = true
    debouncedSave.cancel()
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
  }, [debouncedSave])

  const resumeAutoSave = useCallback(() => {
    isCancelledRef.current = false
  }, [])

  return { markAsSaved, cancelPendingSaves, resumeAutoSave }
}

export default useAutoSave
